import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1771747131849-41056d3d9be3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBnZW9tZXRyeSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NjI5NjYwMnww&ixlib=rb-4.1.0&q=85";

const LINES = ["DAILY CHARTS.", "FRESHLY DRAWN.", "READY TO SHARE."];

export default function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "55%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <motion.div style={reduced ? undefined : { y: bgY }} className="absolute inset-0">
        <img src={HERO_BG} alt="" className="h-[125%] w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/20 to-[#050505]" />
      </motion.div>

      <motion.div style={reduced ? undefined : { y: fgY }} className="relative z-10 px-6 md:px-16 lg:px-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          data-testid="hero-overline"
          className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-[#A1A1A6]"
        >
          <span className="text-[#2997FF]">Dear</span> · <span className="text-[#00E676]">KL</span> ·{" "}
          <span className="text-[#FFD700]">Golden Raja</span> — தினசரி சார்ட்கள்
        </motion.p>
        <h1 className="font-heading text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl lg:text-[7.5rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={reduced ? false : { y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: 0.15 + i * 0.15 }}
              >
                {i === 1 ? <span className="text-[#FFD700]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-8 flex gap-2"
          data-testid="hero-brand-bars"
        >
          <span className="h-1.5 w-16 rounded-full bg-[#2997FF]" />
          <span className="h-1.5 w-16 rounded-full bg-[#00E676]" />
          <span className="h-1.5 w-16 rounded-full bg-[#FFD700]" />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-24 right-6 z-10 max-w-xs text-right md:right-16"
      >
        <p className="text-sm leading-relaxed text-[#A1A1A6] md:text-base">
          Daily Dear, KL & Golden Raja charts. Browse, download and blast them to WhatsApp & Telegram in one tap.
        </p>
        <a
          href="#gallery"
          data-testid="hero-browse-cta"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FFD700] px-7 py-3 text-xs font-bold uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-105"
        >
          Browse Charts
        </a>
      </motion.div>

      <motion.div
        animate={reduced ? undefined : { y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        className="absolute bottom-10 left-6 z-10 text-[#666666] md:left-16"
      >
        <ChevronDown className="h-5 w-5" />
      </motion.div>
    </section>
  );
}
